/**
 * /auth/return — adopts the session forwarded by saas-auth / saas-login.
 *
 * Two arrival shapes are adopted first (SASADM-0081): direct-SSO tokens in the
 * query string and MFA-hand-off tokens in the URL fragment. The bearer is read
 * into memory, the tokens are immediately stripped from the URL, and we navigate
 * to the stashed deep link. When no tokens are forwarded we fall back to the
 * shipped cookie-based silent refresh (`bootRefresh`); on its failure we render
 * the terminal /auth/error page (no auto-loop).
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { bootRefresh, popDeepLink } from '@/auth/silentRefresh';
import { getAccessToken } from '@/auth/authStore';
import { parseReturnTokens } from '@/auth/returnTokens';
import { useAuth } from '@/auth/AuthContext';
import { useCopy } from '@/hooks/useCopy';

export function AuthReturn() {
  const [target, setTarget] = useState<string | null>(null);
  const { setAuthed, refineOperator } = useAuth();
  const ran = useRef(false);
  const { t } = useCopy();

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    void (async () => {
      // 1) Forwarded session (direct-SSO query OR MFA-hand-off fragment).
      const tokens = parseReturnTokens(window.location.search, window.location.hash);
      if (tokens) {
        const expiresAt = tokens.expiresIn
          ? new Date(Date.now() + tokens.expiresIn * 1000).toISOString()
          : null;
        setAuthed(tokens.accessToken, expiresAt);
        // refreshToken is intentionally NOT persisted — the journix_refresh
        // cookie on the AUTH origin is the durable refresh path (authStore has
        // no refresh-token slot; see spec Non-Goals).
        // Strip tokens from the URL so they aren't left in the address bar/history.
        window.history.replaceState(null, '', window.location.pathname);
        // Refine the claims-derived identity against GET /api/auth/me (U3).
        // Fire-and-forget; fails soft (never signs out).
        void refineOperator();
        setTarget(popDeepLink() ?? '/');
        return;
      }
      // 2) Already-authed short-circuit.
      if (getAccessToken()) {
        setTarget(popDeepLink() ?? '/');
        return;
      }
      // 3) Cookie-based silent refresh (fallback).
      const ok = await bootRefresh();
      if (ok) {
        const token = getAccessToken();
        if (token) setAuthed(token, null);
        // Refine the claims-derived identity against GET /api/auth/me (U3).
        void refineOperator();
        setTarget(popDeepLink() ?? '/');
      } else {
        setTarget('/auth/error');
      }
    })();
  }, [setAuthed, refineOperator]);

  if (target) return <Navigate to={target} replace />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2,
      }}
    >
      <LoadingSpinner />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('auth.returning', 'Completing sign-in…')}
      </Typography>
    </Box>
  );
}
